import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Phone, Users, Book, Heart, Shield, Clock, 
  Globe, MapPin, FileText, ArrowRight, ExternalLink 
} from 'lucide-react';

const ResourcesPage = () => {
  const emergencyResources = [
    {
      title: 'National Suicide Prevention Lifeline',
      phone: '988',
      description: '24/7 crisis support for suicidal thoughts and mental health emergencies',
      available: '24/7',
      icon: <Phone className="h-5 w-5" />
    },
    {
      title: 'SAMHSA National Helpline',
      phone: '1-888-968-1014',
      description: 'Treatment referral and information service for substance abuse',
      available: '24/7',
      icon: <Phone className="h-5 w-5" />
    },
    {
      title: 'Crisis Text Line',
      phone: 'Text HOME to 741741',
      description: 'Free, 24/7 support for people in crisis via text message',
      available: '24/7',
      icon: <FileText className="h-5 w-5" />
    },
    {
      title: 'Poison Control Center',
      phone: '1-800-222-1222',
      description: 'Emergency help for overdoses and poisoning incidents',
      available: '24/7',
      icon: <Shield className="h-5 w-5" />
    }
  ];

  const supportGroups = [
    {
      name: 'Alcoholics Anonymous (AA)',
      description: '12-step program for alcohol addiction recovery with meetings worldwide',
      website: 'https://www.aa.org',
      type: 'Alcohol',
      format: 'In-person & Virtual'
    },
    {
      name: 'Narcotics Anonymous (NA)',
      description: '12-step program for drug addiction recovery with global community',
      website: 'https://www.na.org',
      type: 'Drugs',
      format: 'In-person & Virtual'
    },
    {
      name: 'SMART Recovery',
      description: 'Science-based addiction recovery with self-management tools',
      website: 'https://www.smartrecovery.org',
      type: 'All Substances',
      format: 'In-person & Online'
    },
    {
      name: 'Al-Anon',
      description: 'Support for families and friends of people with alcohol problems',
      website: 'https://al-anon.org',
      type: 'Family/Friends',
      format: 'In-person & Virtual'
    },
    {
      name: 'Nar-Anon',
      description: 'Support for families and friends affected by drug addiction',
      website: 'https://www.nar-anon.org',
      type: 'Family/Friends',
      format: 'In-person & Virtual'
    },
    {
      name: 'Celebrate Recovery',
      description: 'Christ-centered recovery program for various addictions and hurts',
      website: 'https://www.celebraterecovery.com',
      type: 'Faith-Based',
      format: 'In-person'
    }
  ];

  const educationalResources = [
    {
      title: 'SAMHSA Publications',
      description: 'Free guides on addiction, treatment, and recovery',
      url: 'https://store.samhsa.gov',
      category: 'Government',
      icon: <Book className="h-5 w-5" />
    },
    {
      title: 'National Institute on Drug Abuse (NIDA)',
      description: 'Research-based information on drug abuse and addiction',
      url: 'https://www.drugabuse.gov',
      category: 'Research',
      icon: <Globe className="h-5 w-5" />
    },
    {
      title: 'Partnership to End Addiction',
      description: 'Resources for families dealing with addiction',
      url: 'https://drugfree.org',
      category: 'Family',
      icon: <Heart className="h-5 w-5" />
    },
    {
      title: 'Addiction Policy Forum',
      description: 'Policy advocacy and education resources',
      url: 'https://www.addictionpolicy.org',
      category: 'Advocacy',
      icon: <Users className="h-5 w-5" />
    }
  ];

  const stateResources = [
    { state: 'California', helpline: '1-844-425-2723', website: 'dhcs.ca.gov' },
    { state: 'Texas', helpline: '1-877-662-8666', website: 'dshs.texas.gov' },
    { state: 'Florida', helpline: '1-844-426-2327', website: 'myflfamilies.com' },
    { state: 'New York', helpline: '1-888-968-1014', website: 'oasas.ny.gov' },
    { state: 'Pennsylvania', helpline: '1-888-968-1014', website: 'ddap.pa.gov' },
    { state: 'Illinois', helpline: '1-833-234-6343', website: 'dhs.state.il.us' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Addiction Recovery Resources
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Comprehensive resources for individuals in recovery, families, and loved ones. 
              Find support groups, educational materials, and emergency help.
            </p>
            
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-4 mb-8">
              <Phone className="h-5 w-5 mr-3" />
              <span className="mr-2">24/7 Help Available:</span>
              <a 
                href="tel:1-888-968-1014"
                className="text-xl font-bold hover:underline"
              >
                1-888-968-1014
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Resources */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Emergency & Crisis Resources
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                If you or someone you know is in crisis, don't wait. These resources provide 
                immediate, confidential support 24 hours a day, 7 days a week.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {emergencyResources.map((resource, index) => (
                <Card key={index} className="border-l-4 border-l-red-500 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className="p-2 bg-red-100 text-red-600 rounded-full mr-3">
                          {resource.icon}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{resource.title}</h3>
                          <Badge className="bg-red-100 text-red-700 text-xs">
                            Available {resource.available}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-4 text-sm">{resource.description}</p>
                    
                    <Button asChild className="w-full bg-red-600 hover:bg-red-700">
                      <a href={resource.phone.startsWith('Text') ? '#' : `tel:${resource.phone.replace(/[^0-9]/g, '')}`}>
                        <Phone className="h-4 w-4 mr-2" />
                        {resource.phone}
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Support Groups */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Support Groups & Peer Support
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Connect with others who understand your journey. Support groups provide 
                ongoing encouragement, accountability, and community in recovery.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {supportGroups.map((group, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg">{group.name}</CardTitle>
                    <div className="flex gap-2">
                      <Badge variant="outline">{group.type}</Badge>
                      <Badge variant="secondary">{group.format}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm mb-4">{group.description}</p>
                    
                    <Button asChild variant="outline" className="w-full">
                      <a href={group.website} target="_blank" rel="noopener noreferrer">
                        <Globe className="h-4 w-4 mr-2" />
                        Visit Website
                        <ExternalLink className="h-3 w-3 ml-2" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Educational Resources */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Educational Resources & Information
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Learn about addiction, treatment options, and recovery with evidence-based 
                information from trusted organizations and research institutions.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {educationalResources.map((resource, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start mb-4">
                      <div className="p-2 bg-blue-100 text-blue-600 rounded-full mr-4">
                        {resource.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-900">{resource.title}</h3>
                          <Badge variant="outline">{resource.category}</Badge>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                        
                        <Button asChild variant="outline" size="sm">
                          <a href={resource.url} target="_blank" rel="noopener noreferrer">
                            Learn More
                            <ExternalLink className="h-3 w-3 ml-2" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* State Resources */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                State-Specific Resources
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Many states offer their own helplines and resources for addiction treatment. 
                Find resources specific to your state or location.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {stateResources.map((resource, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900">{resource.state}</h3>
                      <MapPin className="h-4 w-4 text-gray-400" />
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-600">Helpline: </span>
                        <a 
                          href={`tel:${resource.helpline}`}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {resource.helpline}
                        </a>
                      </div>
                      
                      <div>
                        <span className="text-gray-600">Website: </span>
                        <a 
                          href={`https://${resource.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {resource.website}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button asChild variant="outline" size="lg">
                <Link to="/states">
                  View All State Resources
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Recovery Tools */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">
              Recovery Tools & Apps
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Clock className="h-5 w-5 mr-2 text-green-600" />
                    Sobriety Tracking
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm">
                    <div className="font-medium">I Am Sober App</div>
                    <div className="text-gray-600">Track sobriety milestones and connect with community</div>
                  </div>
                  
                  <div className="text-sm">
                    <div className="font-medium">Sober Grid</div>
                    <div className="text-gray-600">Social network for people in recovery</div>
                  </div>
                  
                  <div className="text-sm">
                    <div className="font-medium">Recovery Path</div>
                    <div className="text-gray-600">Daily motivation and progress tracking</div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Heart className="h-5 w-5 mr-2 text-red-600" />
                    Mental Health Support
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm">
                    <div className="font-medium">Headspace</div>
                    <div className="text-gray-600">Meditation and mindfulness for recovery</div>
                  </div>
                  
                  <div className="text-sm">
                    <div className="font-medium">Calm</div>
                    <div className="text-gray-600">Sleep stories, meditation, and relaxation</div>
                  </div>
                  
                  <div className="text-sm">
                    <div className="font-medium">Sanvello</div>
                    <div className="text-gray-600">Anxiety and mood tracking with coping tools</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Need Help Finding the Right Resources?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our treatment coordinators can help connect you with the right resources, 
            support groups, and treatment options for your specific situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              <a href="tel:1-888-968-1014">
                <Phone className="h-5 w-5 mr-2" />
                Call Now: 1-888-968-1014
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
              <Link to="/get-help">
                Get Help Online
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;